import { CheckCircleFilled, CloseCircleOutlined, InfoCircleFilled, StarFilled } from '@ant-design/icons';
import { Purchases, PURCHASES_ERROR_CODE, PurchasesOffering } from '@revenuecat/purchases-capacitor';
import { useRequest } from 'ahooks';
import { Carousel, Collapse, Modal } from 'antd';
import GGButton from 'components/buttons/GGButton';
import CaregiverAvatarList from 'components/lists/CaregiverAvatarList';
import AppColors from 'config/AppColors';
import useMessagesLimit from 'hooks/useMessagesLimit';
import usePreferences from 'hooks/usePreferences';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { userActions } from 'store/users';
import Platform from 'utils/Platform';

const useStyles = createUseStyles({
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  title: {
    textAlign: 'center',
  },
  advantages: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    marginTop: 32,
    marginBottom: 16,
    '& p': {
      margin: 4,
      textAlign: 'center',
    },
    '& svg': {
      color: AppColors.main,
    },
  },
  advantagesList: {
    display: 'flex',
    flexDirection: 'row',
    gap: 32,
    justifyContent: 'center',
    color: AppColors.text.lightGray,

    '& svg': {
      marginRight: 8,
    },
  },
  whyIsItNotFree: {
    marginTop: 24,
    marginBottom: 16,
    textAlign: 'center',
    borderRadius: 12,
    padding: 12,

    '& svg': {
      marginRight: 4,
    },

    '& .ant-collapse-header': {
      padding: '0 !important',
      color: `${AppColors.text.lightGray} !important`,
    },

    '& .ant-collapse-content-box': {
      padding: '0 !important',
      color: `${AppColors.text.lightGray} !important`,
    },
  },
  testimonial: {
    marginTop: 48,
    textAlign: 'center',
    '& .anticon': {
      color: AppColors.colorMap.average,
    },

    '& p': {
      marginTop: 4,
      marginBottom: 16,
      fontStyle: 'italic',
    },
  },
  testimonialAuthor: {
    color: AppColors.text.discrete,
  },
  pricingOption: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    borderRadius: '10px',
    margin: '20px 0px',
    boxShadow: '0px 5px 0px 0px rgba(0, 0, 0, 0.1)',
    backgroundColor: AppColors.backgroundLightGrey,
    border: `2px solid ${AppColors.backgroundLightGrey}`,

    '& p': {
      margin: 0,
    },
  },
  selectedPricingOption: {
    composes: '$pricingOption',
    backgroundColor: AppColors.backgroundLightGreen,
    boxShadow: '0px 5px 0px 0px rgba(127, 186, 169, 0.7)',
    border: `2px solid ${AppColors.main}`,
  },
  pricingOptionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    textAlign: 'left',
  },
  pricingOptionSubtitle: {
    fontSize: '15px',
  },
  pricingOptionPrice: {
    fontSize: '15px',
    textAlign: 'right',
  },
  secondPricePhase: {
    fontSize: '10px',
    marginTop: 4,
    color: AppColors.text.lightGray,
  },
  conditions: {
    fontSize: '0.9em',
    color: AppColors.text.discrete,
    textAlign: 'center',
  },
  paymentSuccessTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      marginLeft: 16,
      color: AppColors.main,
    },
  },
  paymentFailedTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      marginRight: 12,
      color: AppColors.colorMap.veryBad,
    },
  },
  paymentDescription: {
    fontSize: '1em',
    color: AppColors.text.discrete,
    textAlign: 'center',
    margin: '32px 0px',
  },
  legal: {
    textAlign: 'center',
    fontSize: '0.8em',
    color: AppColors.text.discrete,
    marginTop: '20px',
    '& a': {
      color: AppColors.text.discrete,
    },
  },
});

interface PaywallModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onClose: () => void;
  onFreeMessagesEnabled?: () => void;
  suggestTestIfEligible?: boolean;
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  closeModal,
  onFreeMessagesEnabled,
  suggestTestIfEligible = true,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [displayPage, setDisplayPage] = useState<
    'offers' | 'paymentSucceeded' | 'paymentFailed' | 'freeMessagesEnabled'
  >('offers');
  const [purchaseInProgress, setPurchaseInProgress] = useState(false);
  const dispatch = useDispatch();
  const messagesLimit = useMessagesLimit();

  const [offering, setOffering] = useState<PurchasesOffering | null>(null);

  const { data: offerings, run: getOfferings } = useRequest(() => Purchases.getOfferings(), {
    manual: true,
    onSuccess: offerings => {
      setOffering(offerings.all['offering2']);
    },
  });

  const [selectedPricingOption, setSelectedPricingOption] = useState<string>('monthly');

  const { value: didEnableFreeMessagesValue } = usePreferences(Keys.PAYWALL_DID_ENABLE_FREE_MESSAGES);
  const didEnableFreeMessages = didEnableFreeMessagesValue === true;

  const { run: purchasePackage } = useRequest(pack => Purchases.purchasePackage({ aPackage: pack }), {
    manual: true,
    onSuccess: result => {
      setPurchaseInProgress(false);
      dispatch(userActions.setCurrentUserSubscription(result.customerInfo));
      setDisplayPage('paymentSucceeded');
      posthog.capture('paywall.purchase_succeeded');
    },
    onError: (error: any) => {
      setPurchaseInProgress(false);
      setDisplayPage('paymentFailed');
      if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        // Purchase cancelled
        posthog.capture('paywall.purchase_cancelled');
      } else {
        // Error making purchase
        posthog.capture('paywall.purchase_failed');
      }
    },
  });

  useEffect(() => {
    getOfferings();
  }, []);

  const handlePurchase = () => {
    // Logic to handle purchase or subscription
    setPurchaseInProgress(true);
    if (selectedPricingOption === 'threeMonth') {
      purchasePackage(offering?.threeMonth);
      posthog.capture('paywall.purchase_started', {
        pricing_option: selectedPricingOption,
        price: offering?.threeMonth?.product.price,
        intro_price: offering?.threeMonth?.product.introPrice?.price,
        currency: offering?.threeMonth?.product.currencyCode,
      });
    } else if (selectedPricingOption === 'monthly') {
      purchasePackage(offering?.monthly);
      posthog.capture('paywall.purchase_started', {
        pricing_option: selectedPricingOption,
        price: offering?.monthly?.product.price,
        intro_price: offering?.monthly?.product.introPrice?.price,
        currency: offering?.monthly?.product.currencyCode,
      });
    } else {
      purchasePackage(offering?.weekly);
      posthog.capture('paywall.purchase_started', {
        pricing_option: selectedPricingOption,
        price: offering?.weekly?.product.price,
      });
    }
  };

  const threeMonthIntroPercentage =
    1 - offering?.threeMonth?.product.introPrice?.price / offering?.threeMonth?.product.price;
  const monthlyIntroPercentage = 1 - offering?.monthly?.product.introPrice?.price / offering?.monthly?.product.price;

  const offersDescription = (
    <>
      <div className={styles.logoContainer}>
        <img src={require('assets/logo.png')} width={80} />
      </div>
      <h2 className={styles.title}>{t('paywall.title')}</h2>
      <div className={styles.advantages}>
        <p>{t('paywall.subtitle')}</p>
        <p className={styles.advantagesList}>
          <span>
            <CheckCircleFilled />
            {t('paywall.advantages.7j7')}
          </span>
          <span>
            <CheckCircleFilled />
            {t('paywall.advantages.answersIn24h')}
          </span>
        </p>
      </div>
      <CaregiverAvatarList direction="horizontal" />
      <Carousel autoplay>
        <div className={styles.testimonial}>
          <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
          <p>
            &quot;{t('paywall.testimonials.1')}&quot; - <span className={styles.testimonialAuthor}>Léa</span>
          </p>
        </div>
        <div className={styles.testimonial}>
          <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
          <p>
            &quot;{t('paywall.testimonials.2')}&quot; - <span className={styles.testimonialAuthor}>Camille</span>
          </p>
        </div>
        <div className={styles.testimonial}>
          <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
          <p>
            &quot;{t('paywall.testimonials.3')}&quot; - <span className={styles.testimonialAuthor}>Céline</span>
          </p>
        </div>
      </Carousel>
      <p className={styles.whyIsItNotFree}>
        <Collapse
          ghost
          expandIcon={() => <InfoCircleFilled />}
          items={[
            {
              key: '1',
              label: <>{t('paywall.whyPaywall')}</>,
              children: (
                <p>
                  <Trans i18nKey="paywall.explainWhyPaywall" components={{ br: <br /> }} />
                </p>
              ),
            },
          ]}
        />
      </p>
    </>
  );

  const webPaywallContent = (
    <>
      {offersDescription}
      <p className={styles.conditions}>
        <Trans
          i18nKey="paywall.webPaywall"
          components={{
            appstore: <Link target="_blank" to="https://apps.apple.com/fr/app/gynger/id6503453272" />,
            googleplay: <Link target="_blank" to="https://play.google.com/store/apps/details?id=com.gyngercare.app" />,
          }}
        />
      </p>
    </>
  );

  const offersContent = (
    <>
      {offersDescription}
      <div
        className={selectedPricingOption === 'oneWeek' ? styles.selectedPricingOption : styles.pricingOption}
        onClick={() => setSelectedPricingOption('oneWeek')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('oneWeek');
          }
        }}
      >
        <>
          <div>
            <p className={styles.pricingOptionTitle}>{t('paywall.oneWeekPrice.title')}</p>
            <p className={styles.pricingOptionSubtitle}>{t('paywall.oneWeekPrice.subtitle')}</p>
          </div>
          <p className={styles.pricingOptionPrice}>{offering?.weekly?.product.priceString}</p>
        </>
      </div>
      <div
        className={selectedPricingOption === 'monthly' ? styles.selectedPricingOption : styles.pricingOption}
        onClick={() => setSelectedPricingOption('monthly')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('monthly');
          }
        }}
      >
        {offering?.monthly?.product.introPrice ? (
          <>
            <div>
              <p className={styles.pricingOptionTitle}>{t('paywall.monthlyPrice.title')}</p>
              <p className={styles.pricingOptionSubtitle}>
                {t('paywall.monthlyPrice.offerPercent', {
                  percent: Math.floor(monthlyIntroPercentage * 100),
                })}
              </p>
            </div>
            <p className={styles.pricingOptionPrice}>
              <b>{offering?.monthly?.product.introPrice?.priceString}</b> {t('paywall.perMonth')}
              <br />
              <div className={styles.secondPricePhase}>
                {t('paywall.monthlyPrice.secondPricePhase', {
                  months: offering?.monthly?.product.introPrice?.cycles,
                  priceString: offering?.monthly?.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : (
          <>
            <p className={styles.pricingOptionTitle}>{t('paywall.monthlyPrice.title')}</p>
            <p className={styles.pricingOptionPrice}>
              {offering?.monthly?.product.priceString} {t('paywall.perMonth')}
            </p>
          </>
        )}
      </div>
      <div
        className={selectedPricingOption === 'threeMonth' ? styles.selectedPricingOption : styles.pricingOption}
        onClick={() => setSelectedPricingOption('threeMonth')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('threeMonth');
          }
        }}
      >
        {offering?.threeMonth?.product.introPrice ? (
          <>
            <div>
              <p className={styles.pricingOptionTitle}>{t('paywall.threeMonthPrice.title')}</p>
              <p className={styles.pricingOptionSubtitle}>
                {t('paywall.threeMonthPrice.offerPercent', {
                  percent: Math.floor(threeMonthIntroPercentage * 100),
                })}
              </p>
            </div>
            <p className={styles.pricingOptionPrice}>
              <b>{offering?.threeMonth?.product.introPrice?.priceString}</b> {t('paywall.perQuarter')}
              <br />
              <div className={styles.secondPricePhase}>
                {t('paywall.threeMonthPrice.secondPricePhase', {
                  months: offering?.threeMonth?.product.introPrice?.cycles * 3,
                  priceString: offering?.threeMonth?.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : (
          <>
            <p className={styles.pricingOptionTitle}>{t('paywall.threeMonthPrice.title')}</p>
            <p className={styles.pricingOptionPrice}>
              {offering?.threeMonth?.product.priceString} {t('paywall.perQuarter')}
            </p>
          </>
        )}
      </div>
      <p className={styles.conditions}>{t('paywall.conditions')}</p>
      <p className={styles.conditions}>
        <Trans i18nKey="paywall.doYouHaveACode" components={{ a: <Link to="/settings" /> }} />
      </p>
      <GGButton size="large" block type="primary" onClick={handlePurchase} loading={purchaseInProgress}>
        {selectedPricingOption === 'oneWeek' ? t('paywall.purchaseButtonOneWeek') : t('paywall.purchaseButton')}
      </GGButton>
      {!didEnableFreeMessages && messagesLimit > 0 && suggestTestIfEligible && (
        <GGButton
          style={{ marginTop: 16, height: 'fit-content' }}
          size="large"
          block
          color="discrete"
          type="link"
          onClick={() => {
            if (onFreeMessagesEnabled) {
              onFreeMessagesEnabled();
            }
            setDisplayPage('freeMessagesEnabled');
            posthog.capture('paywall.free_messages_enabled');
          }}
        >
          <div>
            {t('paywall.freeMessagesButton.title')}
            <div style={{ fontSize: '0.7em' }}>
              {messagesLimit} {t('paywall.freeMessagesButton.freeMessages')}
            </div>
          </div>
        </GGButton>
      )}
      <p className={styles.legal}>
        <Link target="_blank" to="https://gyngercare.com/mentionslegales/">
          {t('paywall.termsOfUse')}
        </Link>{' '}
        -{' '}
        <Link target="_blank" to="https://gyngercare.com/mentionslegales/">
          {t('paywall.privacyPolicy')}
        </Link>
      </p>
    </>
  );

  const paymentSucceededContent = (
    <>
      <h2 className={styles.paymentSuccessTitle}>
        {t('paywall.paymentSucceeded.title')} <CheckCircleFilled />
      </h2>
      <p className={styles.paymentDescription}>{t('paywall.paymentSucceeded.description')}</p>

      <GGButton
        size="large"
        block
        type="primary"
        onClick={() => {
          closeModal();
        }}
      >
        {t('paywall.paymentSucceeded.callToAction')}
      </GGButton>
    </>
  );

  const paymentFailedContent = (
    <>
      <h2 className={styles.paymentFailedTitle}>
        <CloseCircleOutlined /> {t('paywall.paymentFailed.title')}
      </h2>
      <p className={styles.paymentDescription}>{t('paywall.paymentFailed.description')}</p>
      <GGButton size="large" block type="primary" onClick={() => setDisplayPage('offers')}>
        {t('paywall.paymentFailed.callToAction')}
      </GGButton>
    </>
  );

  const freeMessagesEnabledContent = (
    <>
      <h2 className={styles.paymentSuccessTitle}>
        {t('paywall.freeMessagesEnabled.title')} <CheckCircleFilled />
      </h2>
      <p className={styles.paymentDescription}>
        {t('paywall.freeMessagesEnabled.description', { count: messagesLimit })}
      </p>

      <GGButton
        size="large"
        block
        type="primary"
        onClick={() => {
          closeModal();
        }}
      >
        {t('paywall.freeMessagesEnabled.callToAction')}
      </GGButton>
    </>
  );

  return (
    <Modal
      open={isOpen}
      afterOpenChange={open => {
        if (open) {
          posthog.capture('paywall.prices_displayed', {
            monthly_price: offering?.monthly?.product.price,
            monthly_intro_price: offering?.monthly?.product.introPrice?.price,
            three_month_price: offering?.threeMonth?.product.price,
            three_month_intro_price: offering?.threeMonth?.product.introPrice?.price,
            currency: offering?.monthly?.product.currencyCode,
          });
        }
      }}
      onCancel={() => {
        onClose();
        posthog.capture('paywall.closed');
      }}
      footer={null}
    >
      {displayPage === 'offers' && <>{Platform.getPlatform() === 'web' ? webPaywallContent : offersContent}</>}
      {displayPage === 'paymentSucceeded' && paymentSucceededContent}
      {displayPage === 'paymentFailed' && paymentFailedContent}
      {displayPage === 'freeMessagesEnabled' && freeMessagesEnabledContent}
    </Modal>
  );
};

export default PaywallModal;
